// Server function that calls the iterpec-search-hotels edge function
// during SSR so the initial HTML contains real hotel results (SEO win).
// Uses a hard timeout so a slow upstream never hangs the page.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import type { IterpecSearchResponse } from "@/types/iterpec";

const inputSchema = z.object({
  destinationId: z.string().min(1),
  checkIn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  checkOut: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  adults: z.number().int().min(1).max(9),
  childrenAges: z.array(z.number().int().min(0).max(17)).default([]),
});

export type SearchHotelsInput = z.infer<typeof inputSchema>;

export type SearchHotelsResult =
  | { ok: true; data: IterpecSearchResponse }
  | { ok: false; reason: "timeout" | "upstream_error" | "missing_config" };

export const searchHotelsServer = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => inputSchema.parse(input))
  .handler(async ({ data }): Promise<SearchHotelsResult> => {
    const base = process.env.SUPABASE_URL;
    const anonKey = process.env.SUPABASE_ANON_KEY;
    if (!base) return { ok: false, reason: "missing_config" };

    const controller = new AbortController();
    // Iterpec upstream is slow (~15-20s per query). Crawlers wait up to 30s,
    // so we set a generous 22s ceiling. Users hitting a fresh search wait too;
    // caching popular search combos (Postgres or KV) is the next step.
    const timeout = setTimeout(() => controller.abort(), 22000);
    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (anonKey) {
        headers.apikey = anonKey;
        headers.Authorization = `Bearer ${anonKey}`;
      }
      const res = await fetch(`${base}/functions/v1/iterpec-search-hotels`, {
        method: "POST",
        headers,
        body: JSON.stringify(data),
        signal: controller.signal,
      });
      if (!res.ok) {
        console.error("[searchHotelsServer] upstream", res.status);
        return { ok: false, reason: "upstream_error" };
      }
      const json = (await res.json()) as IterpecSearchResponse;
      return { ok: true, data: json };
    } catch (err) {
      const isAbort =
        typeof err === "object" && err !== null && "name" in err &&
        (err as { name?: string }).name === "AbortError";
      console.error("[searchHotelsServer] error", err);
      return { ok: false, reason: isAbort ? "timeout" : "upstream_error" };
    } finally {
      clearTimeout(timeout);
    }
  });
